import React, { useState, useEffect } from 'react';
import { SketchPicker } from 'react-color';
import { FaTrash, FaPlus } from 'react-icons/fa';
import { gradientPresets } from '../../config/gradients';
import './GradientPicker.css';

// Helper to parse gradient string
const parseGradient = (gradientStr) => {
  if (!gradientStr || !gradientStr.includes('gradient')) return null;

  let type = 'linear';
  let angle = 180;
  let stops = [];

  if (gradientStr.includes('radial-gradient')) {
    type = 'radial';
  }

  // Remove outer function
  const content = gradientStr.substring(gradientStr.indexOf('(') + 1, gradientStr.lastIndexOf(')'));
  
  // Split by comma, handling potential nested parenthesis (though unlikely for simple hex gradients)
  const parts = content.split(/,(?![^(]*\))/).map(p => p.trim());

  // Check first part for angle/direction
  let startIndex = 0;
  const firstPart = parts[0];
  
  if (type === 'linear') {
    if (firstPart.includes('deg')) {
      angle = parseInt(firstPart);
      startIndex = 1;
    } else if (firstPart.includes('to ')) {
      if (firstPart === 'to top') angle = 0;
      else if (firstPart === 'to right') angle = 90;
      else if (firstPart === 'to bottom') angle = 180;
      else if (firstPart === 'to left') angle = 270;
      else if (firstPart === 'to top right') angle = 45;
      else if (firstPart === 'to bottom right') angle = 135;
      else if (firstPart === 'to bottom left') angle = 225;
      else if (firstPart === 'to top left') angle = 315;
      startIndex = 1;
    }
  } else {
    if (firstPart.includes('circle') || firstPart.includes('at')) {
      startIndex = 1; // Skip shape/position definition for now
    }
  }

  // Parse stops
  for (let i = startIndex; i < parts.length; i++) {
    const part = parts[i];
    // Match color and percentage
    const match = part.match(/(#[0-9a-fA-F]{3,6}|[a-zA-Z]+|rgba?\([^)]+\))\s*(\d+%)?/);
    if (match) {
      const color = match[1];
      let position = match[2] ? parseInt(match[2]) : null;
      stops.push({ color, position });
    }
  }

  // Fill missing positions
  if (stops.length > 0) {
    if (stops[0].position === null) stops[0].position = 0;
    if (stops[stops.length - 1].position === null) stops[stops.length - 1].position = 100;
    
    // Simple interpolation for middle missing stops
    let lastDefinedIdx = 0;
    for (let i = 1; i < stops.length; i++) {
      if (stops[i].position !== null) {
        const startPos = stops[lastDefinedIdx].position;
        const endPos = stops[i].position;
        const count = i - lastDefinedIdx;
        const step = (endPos - startPos) / count;
        
        for (let j = 1; j < count; j++) {
          stops[lastDefinedIdx + j].position = Math.round(startPos + (step * j));
        }
        lastDefinedIdx = i;
      }
    }
  }

  return { type, angle, stops };
};

// Angle Wheel Component
export const AngleWheel = ({ angle, onChange }) => {
  const handleMouseDown = (e) => {
    const wheel = e.currentTarget;
    const rect = wheel.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const calculateAngle = (clientX, clientY) => {
      const deltaX = clientX - centerX;
      const deltaY = clientY - centerY;
      // Calculate angle in degrees (0-360), 0 at top (12 o'clock)
      // Math.atan2(y, x) returns angle in radians from x-axis
      // We want 0 at top, so we shift by 90 degrees
      let deg = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
      if (deg < 0) deg += 360;
      return Math.round(deg);
    };

    const handleMouseMove = (moveEvent) => {
      const newAngle = calculateAngle(moveEvent.clientX, moveEvent.clientY);
      onChange(newAngle);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Initial click update
    const newAngle = calculateAngle(e.clientX, e.clientY);
    onChange(newAngle);
  };

  return (
    <div className="angle-wheel-container">
      <div className="angle-wheel" onMouseDown={handleMouseDown}>
        <div 
          className="angle-indicator" 
          style={{ transform: `rotate(${angle}deg)` }} 
        />
      </div>
      <input
        type="number"
        className="angle-input"
        value={angle}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        min="0"
        max="360"
      />
      <span className="angle-unit">°</span>
    </div>
  );
};

function GradientPicker({ value, onChange }) {
  const [type, setType] = useState('linear');
  const [angle, setAngle] = useState(90);
  const [stops, setStops] = useState([
    { color: '#8D2F88', position: 0 },
    { color: '#6919A2', position: 100 }
  ]);
  const [activeStopIndex, setActiveStopIndex] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Parse existing value
  useEffect(() => {
    if (value) {
      const parsed = parseGradient(value);
      if (parsed) {
        setType(parsed.type);
        setAngle(parsed.angle);
        setStops(parsed.stops);
      }
    }
  }, [value]); // Re-parse if value prop changes

  // Update parent when state changes
  const updateGradient = (newType, newAngle, newStops) => {
    const stopsStr = newStops
      .sort((a, b) => a.position - b.position)
      .map(s => `${s.color} ${s.position}%`)
      .join(', ');
    
    let css = '';
    if (newType === 'linear') {
      css = `linear-gradient(${newAngle}deg, ${stopsStr})`;
    } else {
      css = `radial-gradient(circle, ${stopsStr})`;
    }
    
    onChange(css);
  };

  const handleStopChange = (index, changes) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], ...changes };
    setStops(newStops);
    updateGradient(type, angle, newStops);
  };

  const addStop = () => {
    const newStop = { color: '#ffffff', position: 50 };
    const newStops = [...stops, newStop].sort((a, b) => a.position - b.position);
    setStops(newStops);
    updateGradient(type, angle, newStops);
    setActiveStopIndex(newStops.indexOf(newStop));
  };

  const removeStop = (index) => {
    if (stops.length <= 2) return; // Minimum 2 stops
    const newStops = stops.filter((_, i) => i !== index);
    setStops(newStops);
    updateGradient(type, angle, newStops);
    setActiveStopIndex(null);
  };

  const handlePresetClick = (presetValue) => {
    onChange(presetValue);
    const parsed = parseGradient(presetValue);
    if (parsed) {
      setType(parsed.type);
      setAngle(parsed.angle);
      setStops(parsed.stops);
    }
  };

  const handleStopDrag = (index, e) => {
    e.stopPropagation();
    setActiveStopIndex(index);
    setShowColorPicker(true);

    const bar = e.currentTarget.parentElement;
    const rect = bar.getBoundingClientRect();
    
    const handleMouseMove = (moveEvent) => {
      let percent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      percent = Math.max(0, Math.min(100, percent));
      handleStopChange(index, { position: Math.round(percent) });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div className="gradient-picker">
      {/* Preview & Handles */}
      <div className="gradient-preview-bar">
        <div 
          className="gradient-preview-fill" 
          style={{ background: value || 'linear-gradient(90deg, #fff, #000)' }}
        />
        {stops.map((stop, index) => (
          <div
            key={index}
            className={`gradient-stop-handle ${activeStopIndex === index ? 'active' : ''}`}
            style={{ left: `${stop.position}%` }}
            onMouseDown={(e) => handleStopDrag(index, e)}
          >
            <div className="stop-marker" style={{ backgroundColor: stop.color }} />
            <div className="stop-line" />
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="gradient-controls">
        <div className="control-group">
          <label>Type</label>
          <select 
            className="control-input"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              updateGradient(e.target.value, angle, stops);
            }}
          >
            <option value="linear">Linear</option>
            <option value="radial">Radial</option>
          </select>
        </div>
        {type === 'linear' && (
          <div className="control-group">
            <label>Angle</label>
            <AngleWheel 
              angle={angle} 
              onChange={(newAngle) => {
                setAngle(newAngle);
                updateGradient(type, newAngle, stops);
              }} 
            />
          </div>
        )}
      </div>

      {/* Angle Wheel - for interactive angle adjustment */}
      <AngleWheel 
        angle={angle} 
        onChange={(newAngle) => {
          setAngle(newAngle);
          updateGradient(type, newAngle, stops);
        }}
      />

      {/* Stops List */}
      <div className="stops-list">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
          <label style={{fontSize: 12, fontWeight: 600}}>Color Stops</label>
          <button className="btn-icon" onClick={addStop} title="Add Stop"><FaPlus /></button>
        </div>
        {stops.map((stop, index) => (
          <div 
            key={index} 
            className={`stop-item ${activeStopIndex === index ? 'active' : ''}`}
            onClick={() => {
              setActiveStopIndex(index);
              setShowColorPicker(true);
            }}
          >
            <div className="stop-color-preview" style={{ backgroundColor: stop.color }} />
            <input
              type="number"
              className="stop-position-input"
              value={stop.position}
              onChange={(e) => handleStopChange(index, { position: parseInt(e.target.value) })}
              min="0"
              max="100"
            />
            <span style={{fontSize: 12}}>%</span>
            <div style={{flex: 1}} />
            {stops.length > 2 && (
              <button 
                className="btn-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  removeStop(index);
                }}
              >
                <FaTrash size={10} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Color Picker */}
      {showColorPicker && activeStopIndex !== null && (
        <div className="gradient-color-picker-popover">
          <div className="color-picker-cover" onClick={() => setShowColorPicker(false)} />
          <SketchPicker
            color={stops[activeStopIndex].color}
            onChange={(color) => handleStopChange(activeStopIndex, { color: color.hex })}
          />
        </div>
      )}

      {/* Presets */}
      <div style={{marginTop: 16}}>
        <label style={{fontSize: 12, fontWeight: 600, marginBottom: 8, display: 'block'}}>Presets</label>
        <div className="presets-grid">
          {gradientPresets.map((preset, index) => (
            <div
              key={index}
              className={`preset-item ${value === preset.value ? 'active' : ''}`}
              style={{ background: preset.value }}
              onClick={() => handlePresetClick(preset.value)}
              title={preset.name}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default GradientPicker;
